import React, {Component} from 'react'
import {Input, Typography, Popconfirm, message, Select} from 'antd'
import {PlusOutlined, FolderOutlined, FileTextOutlined, DeleteOutlined, EditOutlined, LeftOutlined, SearchOutlined} from '@ant-design/icons'
import NoteCreationBox from './NoteCreationBox'
import './Note.scss'
import {getNotes, deleteNote, getFolders, createFolder, renameFolder, deleteFolder} from "../../util/APIUtils"

const {Text} = Typography
const { Option } = Select

const SORT_STORAGE_KEY = 'note_sort';
const FOLDER_SORT_KEY = 'folder_sort';

class NotePage extends Component {
    state = {
        folders: [],
        notes: [],
        filteredNotes: [],
        selectedFolder: 'all',
        selectedNote: null,
        searchTerm: '',
        loading: false,
        renamingFolderId: null,
        renamingValue: '',
        newFolderMode: false,
        newFolderName: '',
        isMobile: window.innerWidth <= 768,
        mobileView: 'folders',
        noteSortBy: localStorage.getItem(SORT_STORAGE_KEY + '_by') || 'updatedAt',
        noteSortDir: localStorage.getItem(SORT_STORAGE_KEY + '_dir') || 'desc',
        folderSortDir: localStorage.getItem(FOLDER_SORT_KEY) || 'asc', // asc = A→Z, desc = Z→A
    }

    componentDidMount() {
        this.loadFolders();
        this.loadNotes(null);
        window.addEventListener('resize', this.checkMobile);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.checkMobile);
    }

    checkMobile = () => {
        this.setState({ isMobile: window.innerWidth <= 768 });
    }

    loadFolders() {
        getFolders().then(res => this.setState({ folders: res || [] })).catch(() => {});
    }

    loadNotes(folderId) {
        this.setState({ loading: true });
        const { noteSortBy, noteSortDir } = this.state;
        getNotes(0, 1000, folderId === 'all' ? null : folderId, noteSortBy, noteSortDir)
            .then(res => {
                const notes = res || [];
                // Re-apply active search term after reload so search isn't cleared
                const term = (this.state.searchTerm || '').toLowerCase();
                const filteredNotes = term
                    ? notes.filter(n =>
                        (n.title || '').toLowerCase().includes(term) ||
                        (n.content || '').toLowerCase().includes(term)
                    )
                    : notes;
                this.setState({ notes, filteredNotes, loading: false });
            })
            .catch(() => this.setState({ loading: false }));
    }

    selectFolder = (folderId) => {
        this.setState({ selectedFolder: folderId, selectedNote: null, searchTerm: '',
            mobileView: 'notes' });
        this.loadNotes(folderId === 'all' ? null : folderId);
    }

    onSearch = (e) => {
        const raw = e?.target?.value ?? '';
        const term = raw.toLowerCase();
        this.setState(prev => ({
            searchTerm: raw,
            filteredNotes: term
                ? prev.notes.filter(n =>
                    (n.title || '').toLowerCase().includes(term) ||
                    (n.content || '').toLowerCase().includes(term)
                )
                : prev.notes
        }));
    }

    onAdd = () => {
        const { selectedFolder, folders } = this.state;
        const folder = selectedFolder !== 'all'
            ? folders.find(f => f.id === selectedFolder) || null
            : null;
        const newNote = {
            id: 'temp-' + Date.now(),
            title: '',
            content: '',
            tags: [],
            folder: folder ? { id: folder.id, name: folder.name } : null,
            isTemp: true
        };
        this.setState(prev => ({
            notes: [newNote, ...prev.notes],
            filteredNotes: [newNote, ...prev.filteredNotes],
            selectedNote: newNote,
            mobileView: 'editor'
        }));
    }

    onSelectNote = (note) => this.setState({ selectedNote: note, mobileView: 'editor' })

    onUpdateNote = (updatedNote) => {
        this.setState({ selectedNote: updatedNote });
        this.loadNotes(this.state.selectedFolder === 'all' ? null : this.state.selectedFolder);
    }

    onDeleteNote = (noteId) => {
        deleteNote(noteId).then(() => {
            message.success('Deleted');
            this.setState(prev => ({
                notes: prev.notes.filter(n => n.id !== noteId),
                filteredNotes: prev.filteredNotes.filter(n => n.id !== noteId),
                selectedNote: prev.selectedNote?.id === noteId ? null : prev.selectedNote
            }));
        }).catch(() => message.error('Failed to delete'));
    }

    onAddFolder = () => this.setState({ newFolderMode: true, newFolderName: '' })

    onConfirmNewFolder = () => {
        const { newFolderName } = this.state;
        if (!newFolderName.trim()) { this.setState({ newFolderMode: false }); return; }
        createFolder(newFolderName.trim()).then(folder => {
            this.setState(prev => ({
                folders: [...prev.folders, folder],
                newFolderMode: false,
                newFolderName: ''
            }));
        }).catch(() => message.error('Failed to create folder'));
    }

    onStartRename = (folder) => this.setState({ renamingFolderId: folder.id, renamingValue: folder.name })

    onConfirmRename = () => {
        const { renamingFolderId, renamingValue } = this.state;
        if (!renamingValue.trim()) { this.setState({ renamingFolderId: null }); return; }
        renameFolder(renamingFolderId, renamingValue.trim()).then(updated => {
            this.setState(prev => ({
                folders: prev.folders.map(f => f.id === updated.id ? updated : f),
                renamingFolderId: null
            }));
        }).catch(() => message.error('Failed to rename'));
    }

    onDeleteFolder = (folderId) => {
        deleteFolder(folderId).then(() => {
            this.setState(prev => ({
                folders: prev.folders.filter(f => f.id !== folderId),
                selectedFolder: prev.selectedFolder === folderId ? 'all' : prev.selectedFolder
            }));
            if (this.state.selectedFolder === folderId) this.loadNotes(null);
        }).catch(() => message.error('Failed to delete folder'));
    }

    formatDate = (dateString) => {
        if (!dateString) return '';
        const d = new Date(dateString);
        const now = new Date();
        const diff = now - d;
        if (diff < 86400000) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        if (diff < 604800000) return d.toLocaleDateString([], { weekday: 'short' });
        return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }

    renderNoteList() {
        const { folders, filteredNotes, selectedFolder, selectedNote, searchTerm, loading, noteSortBy, noteSortDir } = this.state;
        const folderItems = [{ id: 'all', name: 'All Notes' }, ...folders];
        return (
            <>
                <div className="macos-notelist-header">
                    <span className="macos-notelist-title">
                        {folderItems.find(f => f.id === selectedFolder)?.name || 'Notes'}
                    </span>
                    <button className="macos-icon-btn" onClick={this.onAdd}><FileTextOutlined />+</button>
                </div>
                <div className="macos-notelist-search">
                    <Input prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                        placeholder="Search" value={searchTerm} onChange={this.onSearch} allowClear />
                </div>
                <div style={{ padding: '6px 12px', borderBottom: '1px solid #e6f0ff', background: '#f5f8ff' }}>
                    <Select
                        size="small"
                        value={noteSortBy + '_' + noteSortDir}
                        onChange={val => {
                            const [by, dir] = val.split('_');
                            localStorage.setItem(SORT_STORAGE_KEY + '_by', by);
                            localStorage.setItem(SORT_STORAGE_KEY + '_dir', dir);
                            this.setState({ noteSortBy: by, noteSortDir: dir }, () =>
                                this.loadNotes(this.state.selectedFolder)
                            );
                        }}
                        style={{ width: '100%', fontSize: 12 }}
                    >
                        <Option value="updatedAt_desc">Last modified ↓</Option>
                        <Option value="updatedAt_asc">Last modified ↑</Option>
                        <Option value="createdAt_desc">Newest first</Option>
                        <Option value="createdAt_asc">Oldest first</Option>
                        <Option value="title_asc">Title A → Z</Option>
                        <Option value="title_desc">Title Z → A</Option>
                    </Select>
                </div>
                <div className="macos-notelist-items">
                    {loading && <div style={{ padding: '16px', color: '#8c8c8c' }}>Loading...</div>}
                    {!loading && filteredNotes.length === 0 && (
                        <div style={{ padding: '16px', color: '#8c8c8c', textAlign: 'center' }}>No notes</div>
                    )}
                    {filteredNotes.map(note => (
                        <div key={note.id}
                            className={`macos-note-item ${selectedNote?.id === note.id ? 'active' : ''}`}
                            onClick={() => this.onSelectNote(note)}>
                            <div className="macos-note-item-title">
                                {note.title || note.content?.substring(0, 30) || 'New Note'}
                            </div>
                            <div className="macos-note-item-meta">
                                <span className="macos-note-item-date">{this.formatDate(note.updatedAt)}</span>
                                <span className="macos-note-item-preview">{(note.content || '').substring(0, 40)}</span>
                            </div>
                            <Popconfirm title="Delete note?"
                                onConfirm={e => { e.stopPropagation(); this.onDeleteNote(note.id); }}
                                okText="Yes" cancelText="No">
                                <button className="macos-note-delete-btn" onClick={e => e.stopPropagation()}>
                                    <DeleteOutlined />
                                </button>
                            </Popconfirm>
                        </div>
                    ))}
                </div>
            </>
        );
    }

    renderFolders() {
        const { folders, selectedFolder, renamingFolderId, renamingValue, newFolderMode, newFolderName, folderSortDir } = this.state;

        const sortedFolders = [...folders].sort((a, b) =>
            folderSortDir === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name)
        );

        const folderItems = [
            { id: 'all', name: 'All Notes', icon: '📋' },
            ...sortedFolders.map(f => ({ ...f, icon: '📁' }))
        ];
        return (
            <>
                <div className="macos-folders-header">
                    <span className="macos-folders-title">Folders</span>
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                        <button className="macos-icon-btn" title={folderSortDir === 'asc' ? 'A→Z' : 'Z→A'}
                            onClick={() => this.setState(p => {
                                const next = p.folderSortDir === 'asc' ? 'desc' : 'asc';
                                localStorage.setItem(FOLDER_SORT_KEY, next);
                                return { folderSortDir: next };
                            })}>
                            {folderSortDir === 'asc' ? '↑' : '↓'}
                        </button>
                        <button className="macos-icon-btn" onClick={this.onAddFolder}><FolderOutlined />+</button>
                    </div>
                </div>
                {newFolderMode && (
                    <div className="macos-folder-new">
                        <Input autoFocus size="small" value={newFolderName}
                            onChange={e => this.setState({ newFolderName: e.target.value })}
                            onPressEnter={this.onConfirmNewFolder} onBlur={this.onConfirmNewFolder}
                            placeholder="Folder name" />
                    </div>
                )}
                <div className="macos-folder-list">
                    {folderItems.map(folder => (
                        <div key={folder.id}
                            className={`macos-folder-item ${selectedFolder === folder.id ? 'active' : ''}`}
                            onClick={() => this.selectFolder(folder.id)}>
                            <span className="macos-folder-icon">{folder.icon}</span>
                            {renamingFolderId === folder.id ? (
                                <Input autoFocus size="small" value={renamingValue}
                                    onChange={e => this.setState({ renamingValue: e.target.value })}
                                    onPressEnter={this.onConfirmRename} onBlur={this.onConfirmRename}
                                    onClick={e => e.stopPropagation()} style={{ flex: 1 }} />
                            ) : (
                                <span className="macos-folder-name">{folder.name}</span>
                            )}
                            {folder.id !== 'all' && renamingFolderId !== folder.id && (
                                <div className="macos-folder-actions" onClick={e => e.stopPropagation()}>
                                    <EditOutlined onClick={() => this.onStartRename(folder)} />
                                    <Popconfirm title="Delete folder?" onConfirm={() => this.onDeleteFolder(folder.id)} okText="Yes" cancelText="No">
                                        <DeleteOutlined style={{ color: '#ff4d4f' }} />
                                    </Popconfirm>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </>
        );
    }

    render() {
        const { selectedNote, isMobile, mobileView, selectedFolder, folders } = this.state;
        const folderItems = [{ id: 'all', name: 'All Notes' }, ...folders];

        if (isMobile) {
            return (
                <div className="macos-notes-container">
                    <div className="macos-mobile">
                        {mobileView === 'folders' && (
                            <div className="macos-mobile-panel">
                                <div className="macos-folders macos-mobile-full">
                                    {this.renderFolders()}
                                </div>
                            </div>
                        )}

                        {mobileView === 'notes' && (
                            <div className="macos-mobile-panel">
                                <div className="macos-mobile-topbar">
                                    <button className="macos-mobile-back" onClick={() => this.setState({ mobileView: 'folders' })}>
                                        <LeftOutlined /> Folders
                                    </button>
                                    <span className="macos-mobile-topbar-title">
                                        {folderItems.find(f => f.id === selectedFolder)?.name || 'Notes'}
                                    </span>
                                </div>
                                <div className="macos-notelist macos-mobile-full">
                                    {this.renderNoteList()}
                                </div>
                            </div>
                        )}

                        {mobileView === 'editor' && (
                            <div className="macos-mobile-panel">
                                <div className="macos-mobile-topbar" style={{ flexShrink: 0 }}>
                                    <button className="macos-mobile-back" onClick={() => this.setState({ mobileView: 'notes' })}>
                                        <LeftOutlined /> Notes
                                    </button>
                                </div>
                                <div className="macos-editor macos-mobile-full" style={{ flex: 1, overflow: 'hidden' }}>
                                    {selectedNote
                                        ? <NoteCreationBox note={selectedNote} onUpdate={this.onUpdateNote} />
                                        : <div className="macos-editor-empty"><FileTextOutlined style={{ fontSize: '48px', color: '#d9d9d9' }} /></div>
                                    }
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        return (
            <div className="macos-notes-container">
            <div className="macos-notes">
                {/* Column 1: Folders */}
                <div className="macos-folders">
                    {this.renderFolders()}
                </div>

                {/* Column 2: Notes list */}
                <div className="macos-notelist">
                    {this.renderNoteList()}
                </div>

                {/* Column 3: Editor */}
                <div className="macos-editor">
                    {selectedNote ? (
                        <NoteCreationBox note={selectedNote} onUpdate={this.onUpdateNote} />
                    ) : (
                        <div className="macos-editor-empty">
                            <FileTextOutlined style={{ fontSize: '48px', color: '#d9d9d9' }} />
                            <div style={{ color: '#8c8c8c', marginTop: '12px' }}>Select a note or create a new one</div>
                        </div>
                    )}
                </div>
            </div>
            </div>
        )
    }
}

export default NotePage
